import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AuthService } from '../../services/auth/auth.service';
import { AppInitService } from '../../services/system/app-init.service';
import { Camada } from '../../types/camada.types';
import {
  isRuralOviRoute,
  PVG_PORTAL_KEY,
  Projeto,
  TIPO_PROJETO,
} from '../../types/projeto.types';
import { Usuario } from '../../types/usuario.types';
import * as ProjectsActions from '../projects/projects.actions';
import { ProjectsFacade } from '../projects/projects.facade';
import { MapLayer } from '../projects/projects.types';
import { AuthFacade } from './auth.facade';
import * as AuthActions from './auth.actions';
import { UserPermissionsState } from './auth.reducer';

@Injectable()
export class AuthEffects {
  userLogin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userLogin),
      switchMap(({ email, password }) =>
        this.authService.login({ email, password }).pipe(
          map((user: Usuario) => AuthActions.userLoginSuccess({ user })),
          catchError((error) => of(AuthActions.userLoginFail({ error })))
        )
      )
    )
  );

  userLoginSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.userLoginSuccess),
        withLatestFrom(this.projectsFacade.mode$),
        tap(async ([{ user }, mode]) => {
          localStorage.setItem('currentUser', JSON.stringify(user));
          const redirect = this.router.parseUrl(this.router.url).queryParams[
            'redirect'
          ];
          await (typeof redirect === 'string' && redirect.startsWith('/')
            ? this.router.navigateByUrl(redirect)
            : this.router.navigate([`/${mode}`, 'map']));
          await this.appInitService.init();
        })
      ),
    { dispatch: false }
  );

  userLogout$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.userLogout),
        tap(() => {
          localStorage.removeItem('currentUser');
          this.router.navigate(['/auth/login']);
        })
      ),
    { dispatch: false }
  );

  getUserPermission$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getUserPermission),
      switchMap(() =>
        this.authService.getUserPermission().pipe(
          map((permissions: UserPermissionsState) =>
            AuthActions.getUserPermissionSuccess({ permissions })
          ),
          catchError((error) =>
            of(AuthActions.getUserPermissionFail({ error }))
          )
        )
      )
    )
  );

  // PUBLIC TOKENS
  getPublicTokens$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getPublicTokens),
      switchMap(({ subdomain }) =>
        this.authService.getPublicToken(subdomain).pipe(
          map((publicTokens: any) =>
            AuthActions.getPublicTokensSuccess({ publicTokens })
          ),
          catchError((error) => of(AuthActions.getPublicTokensFail({ error })))
        )
      )
    )
  );

  getPublicTokenSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getPublicTokensSuccess),
      tap(({ publicTokens }) => {
        try {
          localStorage.setItem('publicTokens', JSON.stringify(publicTokens));
        } catch {}
      }),
      map(() => ProjectsActions.getProjectsIds())
    )
  );

  getPublicTokenFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.getPublicTokensFail),
        tap(() => this.router.navigate(['not-found', 'cidade']))
      ),
    { dispatch: false }
  );
  // -- PUBLIC TOKENS

  // PROJECTS
  getProjectsIds$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ProjectsActions.getProjectsIds),
      withLatestFrom(this.projectsFacade.mode$, this.authFacade.user$),
      switchMap(([_, mode, user]) => {
        const pathname =
          typeof window !== 'undefined' ? window.location.pathname : '';
        const isRuralOvi =
          isRuralOviRoute(this.router.url) || isRuralOviRoute(pathname);
        const isOuv =
          isRuralOvi ||
          mode === PVG_PORTAL_KEY ||
          this.router.url.includes(`/${PVG_PORTAL_KEY}`) ||
          pathname.includes(`/${PVG_PORTAL_KEY}`);
        const resolvedMode = isOuv ? PVG_PORTAL_KEY : mode;
        if (isOuv && mode !== PVG_PORTAL_KEY) {
          this.projectsFacade.dispatch(
            ProjectsActions.setPortalMode({ mode: PVG_PORTAL_KEY })
          );
        }
        const params = isOuv
          ? {
              showOuv: true,
              noForms: true,
              representacao: 'PVGEO',
            }
          : {
              showIcad: true,
              ...(!user && { publicoIcad: true }),
              noForms: true,
            };
        const projectType = isRuralOvi
          ? TIPO_PROJETO.OMI_RURAL
          : isOuv
          ? `${TIPO_PROJETO.PVGEO},${TIPO_PROJETO.OUV}`
          : TIPO_PROJETO.ICAD_DEFAULT;

        const sources$: Observable<[Camada[], Projeto[], any]> = isRuralOvi
          ? this.authService
              .getProjectsByType(projectType, true)
              .pipe(
                map((projects): [Camada[], Projeto[], any] => [
                  this.authService.mapProjectsToCamadas(projects),
                  projects,
                  resolvedMode,
                ])
              )
          : combineLatest([
              this.authService.getCamadas(params),
              this.authService.getProjectsByType(projectType),
              of(resolvedMode),
            ]);

        return sources$.pipe(
          switchMap(([camadas, projects, currentMode]) => {
            if (!isOuv && (!camadas || camadas.length === 0)) {
              this.router.navigate(['/auth/login']);
              return EMPTY;
            }
            
            return combineLatest([
              of(camadas),
              of(projects),
              this.authService.mapCamadasToMapLayers(camadas),
              of(currentMode),
            ]);
          }),
          catchError((error) => {
            if (!isOuv) {
              this.router.navigate(['/auth/login']);
            }
            return EMPTY;
          })
        );
      }),
      switchMap(([camadas, projectsList, layers, mode]: any) => {
        const projects =
          projectsList?.length > 0
            ? (projectsList as Projeto[])
            : this.authService.mapCamadasToProjects(camadas as Camada[]);

        const pvgeo = projects.find(
          ({ tipoProjeto }) =>
            tipoProjeto === TIPO_PROJETO.PVGEO ||
            tipoProjeto === TIPO_PROJETO.OMI_RURAL
        );

        const icad = projects.find(
          ({ tipoProjeto }) =>
            [
              TIPO_PROJETO.ICAD_DEFAULT,
              TIPO_PROJETO.ICAD,
              TIPO_PROJETO.ICAD2,
            ].includes(tipoProjeto as any)
        );
        let city = '';
        if (pvgeo) {
          city = pvgeo.cidade;
        }

        if (icad) {
          if (icad.cidade) {
            city = icad.cidade;
          }
        }

        let configuracao = null;
        if (mode === PVG_PORTAL_KEY) {
          try {
            configuracao = JSON.parse(pvgeo?.configuracao || '{}') || null;
          } catch (error) {
            console.error(
              '[auth.effects] Erro ao fazer JSON.parse em pvgeo.configuracao',
              pvgeo?.configuracao,
              error
            );
            configuracao = null;
          }
        } else if (mode === 'icad') {
          try {
            configuracao = JSON.parse(icad?.configuracao || '{}') || null;
          } catch (error) {
            console.error(
              '[auth.effects] Erro ao fazer JSON.parse em icad.configuracao',
              icad?.configuracao,
              error
            );
            configuracao = null;
          }
        }

        return [
          ProjectsActions.setConfiguracao({
            configuracao,
          }),
          AuthActions.getOUVLayersSuccess({ layers: layers as MapLayer[] }),
          ProjectsActions.getProjectsIdsSuccess({
            ids: this.authService.mapProjectsToIds(projects),
          }),
          ProjectsActions.setCity({
            city,
          }),
        ];
      })
    )
  );
  // -- PROJECTS

  getOUVLayersSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.getOUVLayersSuccess),
      // tap(() => this.authService.getUserPermission()),
      map(({ layers }) => ProjectsActions.setProjectsLayers({ layers }))
    )
  );

  // -- LAYERS

  userCheckLogged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AuthActions.userCheckLogged),
      map(() => {
        const userJSON = localStorage.getItem('currentUser');
        if (userJSON) {
          try {
            const user = JSON.parse(userJSON);
            return AuthActions.userCheckLoggedSuccess({ user });
          } catch (error) {
            console.error(
              '[auth.effects] Erro ao fazer JSON.parse em currentUser',
              userJSON,
              error
            );
            return AuthActions.userCheckLoggedFail();
          }
        } else {
          return AuthActions.userCheckLoggedFail();
        }
      })
    )
  );

  ngrxOnInitEffects(): Action {
    return AuthActions.userCheckLogged();
  }

  constructor(
    private actions$: Actions,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute,
    private projectsFacade: ProjectsFacade,
    private authFacade: AuthFacade,
    private appInitService: AppInitService
  ) {}
}
